<template>
<div class="chapter">
<div class="inner_page">
<h2 class="ph">II.</h2>
<p class="calibre11">Barely a month after his graduation, even before he had entirely
completed the postgraduate tours of duty mentioned by Von Hohendorff,
Kinnison was summoned to Prime Base by no less a personage than
Port Admiral Haynes himself. There, in the admiral's private aëro,
whose flaring lights cleared a path as though by magic through the
swarming traffic, the novice and the veteran flew slowly over the vast
establishment of the base.</p>
<p class="calibre11">Shops and factories, citylike barracks, landing fields stretching
beyond the far horizon; flying craft ranging from tiny, one-man
helicopters through small and large scouts, patrol ships and cruisers
up to the immense, globular superdreadnaughts of space—all these were
observed and commented upon. Finally, the aëro landed beside a long,
comparatively low building—a structure heavily guarded, inside the
base although it was—within which Kinnison saw a thing that fairly
snatched away his breath.</p>
<p class="calibre11">A space ship it was—but what a ship! In bulk it was vastly larger even
than the superdreadnaughts of the patrol; but, unlike them, it was,
in shape, a perfect teardrop, streamlined to the ultimate possible
degree.<a id="FNanchor_1_1"></a><span class="fnanchor" href="#/Text/Section0025#Footnote_1_1" title="Link target outside of Sample">[1]</span></p>
<p class="calibre11">"What do you think of her?" the port admiral asked.</p>
<p class="calibre11">"Think of her!" The young officer gulped twice before he attained
coherence. "I can't put it in words, sir; but some day, if I live long
enough and develop enough force, I hope to command a ship like that."</p>
<p class="calibre11">"Sooner than you think, Kinnison," Haynes told him, flatly. "You are in
command of her beginning to-morrow morning."</p>
<p class="calibre11">"Huh? Me?" Kinnison exclaimed, but sobered quickly. "Oh, I see, sir.
It takes ten years of proved accomplishment to rate command of a
first-class enforcement vessel, and I have no rating at all. You have
already intimated that this ship is experimental. There is, then,
something about her that is new and untried, and so dangerous that you
do not want to risk an experienced commander in her. I am to give her
a work-out, and if I can bring her back in one piece I turn her over
to her real captain. But that's all right with me, admiral—thanks a
lot for picking me out. What a chance! <i class="calibre12">What</i> a chance!" Kinnison's eye
gleamed at the prospect of even a brief command of such a creation.</p>
<p class="calibre11">"Right—and wrong," the old admiral made surprising answer. "It is
true that she is new, untried, and dangerous, so much so that we are
unwilling to give her to any of our present captains. No, she is not
really new, either. Rather, her basic idea is so old that it has been
abandoned for centuries. She uses explosives, of a type that cannot be
tried out fully except in actual combat. Her primary weapon is what we
have called the 'Q-gun.' The propellent is heptadetonite; the shell
carries a charge of twenty metric tons of duodecaplylatomate."</p>
<p class="calibre11">"But, sir——" Kinnison began.</p>
<p class="calibre11">"Just a minute, I'll go into that later. While your premises were
correct, your conclusion is not. You graduated No. 1, and in every
respect, save experience, you are as well qualified to command as is
any captain of the fleet; and since the <i class="calibre12">Brittania</i> is such a radical
departure from any conventional type, battle experience is not a
prerequisite. Therefore, if she holds together through one engagement
she is yours for good. In other words, to make up for the possibility
of having yourself scattered all over space, you have a chance to win
that ten years' rating you mentioned a minute ago, all in one trip.
Fair enough?"</p>
<p class="calibre11">"Fair? It's fine—wonderful! And thanks a——"</p>
<p class="calibre11">"Never mind the thanks until you get back. You were about to comment,
I believe, upon the impossibility of using explosives against a free
opponent?"</p>
<p class="calibre11">"It can't be impossible, of course, since the <i class="calibre12">Brittania</i> has been
built. I just don't quite see how it could have been made effective."</p>
<p class="calibre11">"You lock to the pirate with tractors, screen to screen—dex about ten
kilometers. You blast a hole through his screens to his wall shield.
The muzzle of the Q-gun mounts an annular multiplex projector which
puts out a Q-type tube of force—Q47SM9, to be exact. As you can see
from the type formula, this helix extends the gun barrel from ship to
ship and confines the propellent gases behind the projectile, where
they belong. When the shell strikes the wall shield of the pirate and
detonates, <i class="calibre12">something</i> will have to give way.</p>
<p class="calibre11">"The tube and tractors, being pure force and computed for this
particular combination of explosions, will hold; and our physicists
have calculated that the ten-kilometer column of inert propellent
gases will offer so much inertia and resistance that any possible wall
shield will have to go down. That is the point that cannot be tried out
experimentally. It is quite within the bounds of possibility that the
pirates may have been able to develop wall screens as powerful as our
Q-type helices.</p>
<p class="calibre11">"It should not be necessary to point out to you that if they <i class="calibre12">have</i>
been able to develop a wall shield that will stand up under detonating
duodecaplylatomate, the back blast through the breech of the Q-gun will
blow the <i class="calibre12">Brittania</i> apart as though she were made of matchwood. That
is only one of the chances—and perhaps not the greatest one—that you
and your crew will have to take. They are all volunteers, by the way,
and will get plenty of extra rating if they come through alive. Do you
want the job?"</p>
<p class="calibre11">"You don't have to ask me that, chief—you <i class="calibre12">know</i> I want it!"</p>
<p class="calibre11">"Of course, but I had to go through the formality of asking, sometime.
But to get on with the discussion. This pirate situation is entirely
out of control, as you already know. We don't even know whether Boskone
is a reality, a figurehead, a symbol, or simply a figment of somebody's
imagination. But whoever or whatever Boskone really is, some being or
some group of beings has perfected a mighty efficient organization of
outlaws; so efficient that we haven't been able to locate their main
base.</p>
<p class="calibre11">"You may as well know now a fact that is not yet public property; that
even convoyed vessels are no longer safe. The pirates have developed
ships of a new and extraordinary type; ships that are much faster than
our heavy battleships, and yet vastly more heavily armed than our fast
cruisers. Thus, they can outfight any enforcement vessel that can catch
them, and can outrun anything of ours armed heavily enough to stand up
against their beams."</p>
<p class="calibre11">"That accounts for the recent heavy losses," Kinnison mused.</p>
<p class="calibre11">"Yes," Haynes went on, grimly. "Ship after ship of our best has been
blasted out of the ether, doomed before it pointed a beam, and more
will be. We cannot force an engagement on our terms; we must fight on
theirs.</p>
<p class="calibre11">"That is the present intolerable situation. We <i class="calibre12">must</i> learn what
the pirates' new power system is. Our scientists say that it may
be anything, from cosmic-energy receptors and converters down to a
controlled space warp—whatever that may be. Anyway, they haven't
been able to duplicate it, so it is up to us to find out what it is.
The <i class="calibre12">Brittania</i> is the tool our engineers have designed to get that
information. She is the fastest thing in space, developing at full
blast an inert acceleration of <i class="calibre12">ten gravities</i>. Figure out for yourself
what velocity that means free in open space!"</p>
<p class="calibre11">"You have just said that we can't have everything in one ship,"
Kinnison said, thoughtfully. "What did they sacrifice to get that
speed?"</p>
<p class="calibre11">"All the conventional offensive armament," Haynes replied frankly. "She
has no long-range beams at all, and only enough short-range stuff to
help drive the Q-helix through the enemy's screens. Practically her
only offense is the Q-gun. But she has plenty of defensive screens; she
has speed enough to catch anything afloat; and she has the Q-gun—which
we hope will be enough.</p>
<p class="calibre11">"Now we'll go over the general plan of action. The engineers will go
into all the technical details with you, during a test flight that
will last as long as you like. When you and your crew are thoroughly
familiar with every phase of her operation, bring the engineers back
here to base and go out on patrol.</p>
<p class="calibre11">"Somewhere in the galaxy you will find a pirate vessel of the new type.
You lock to him, as I said before. You attach the Q-gun well forward,
being sure that the point of attachment is far enough away from the
power rooms so that the essential mechanisms will not be destroyed.
You board and storm—another revival of the technique of older times.
Specialists in your crew, who will have done nothing much up to that
time, will then find out what our scientists want to know. If at all
possible, they will send it in instantly via tight-beam communicator.
If, because of distance or for any other reason, it should be
impossible for them to communicate, the whole thing is again up to you."</p>
<p class="calibre11">The port admiral paused, his eyes boring into those of the younger man,
then went on impressively: "That information <i class="calibre12">must</i> get back to base.
If it does not, the <i class="calibre12">Brittania</i> is a failure; we will be right back
where we started from; the slaughter of our men and the destruction
of our ships will continue unchecked. As to how you are to do it,
we cannot give you even general instructions. All I can say is that
you have the most important assignment in the universe to-day, and
repeat—<i class="calibre12">that information must get back to base</i>. Now come aboard and
meet your crew and the engineers."</p>
<p class="calibre11">Under the expert tutelage of the designers and builders of the
<i class="calibre12">Brittania</i> Vice Commander Kinnison drove her hither and thither
through the trackless wastes of the galaxy<a id="FNanchor_2_2"></a><span class="fnanchor" href="#/Text/Section0025#Footnote_2_2" title="Link target outside of Sample">[2]</span>. Inert and "free," under
every possible degree of power he maneuvered her; attacking imaginary
foes and actual meteorites with equal zeal. Maneuvered and attacked
until he and his ship were one; until he reacted automatically to
her slightest demand; until he and every man of his eager and highly
trained crew knew to the final volt and to the ultimate ampere her
Gargantuan capacity both to give it and to take it.</p>
<p class="calibre11">Then and only then did he return to base, unload the engineers, and
set out upon the quest. Trail after trail he followed, but all were
cold. Alarm after alarm he answered, but always he arrived too late;
arrived to find gutted merchantmen and riddled enforcement vessel, with
no life in either and with nothing to indicate in which direction the
marauders might have gone.</p>
<p class="calibre11">Finally, however: "QBT! Calling QBT!" The <i class="calibre12">Brittania's</i> code call
blared from the sealed-band speaker, and a string of numbers
followed—the spatial coördinates of the luckless vessel's position.</p>
<p class="calibre11">Chief Pilot Henry Henderson punched the figures upon his locator, and
in the "tank"—the enormous, minutely cubed model of the galaxy—there
appeared a redly brilliant point of light. Kinnison rocketed out of his
narrow bunk, digging the sleep out of his eyes, and shot himself into
his place beside the pilot.</p>
<p class="calibre11">"Right in our laps!" he exulted. "Scarcely ten light years away! Start
scrambling the ether!" And as the vengeful cruiser darted toward the
scene of depredation all space became filled with blast after blast of
static interference through which, it was hoped, the pirate could not
summon the help he was so soon to need.</p>
<p class="calibre11">But that howling static gave the pirate commander pause. Surely this
was something new? Before him lay a richly laden freighter, its two
convoying enforcement ships already practically <i class="calibre12">hors de combat</i>. A few
more minutes and the prize would be his. Nevertheless, he darted away,
swept the ether with his detectors, saw the <i class="calibre12">Brittania</i>, and turned in
headlong flight. For if this streamlined freighter was sufficiently
convinced of its prowess to try to blanket the ether against <i class="calibre12">him</i>,
that information was something that Boskone would value far above one
shipload of material wealth.</p>
<p class="calibre11">But the pirate craft was now upon the visiplates of the <i class="calibre12">Brittania</i>,
and, entirely ignoring the crippled space ships, Henderson flung his
vessel after the other. Manipulating his incredibly complex controls
purely by touch, the while staring into his plate not only with his
eyes, but with every fiber of his being as well, he hurled his huge
mount hither and thither in frantic leaps. After what seemed an age
he snapped down a toggle switch and relaxed long enough to grin at
Kinnison.</p>
<p class="calibre11">"Holding 'em?" the young commander demanded.</p>
<p class="calibre11">"Got 'em, skipper," the pilot replied, positively. "It was touch and go
for ninety seconds, but I've got a CRX tracer on him now at full pull.
He can't put out enough jets to get away from <i class="calibre12">that</i>. I can hold him
forever!"</p>
<p class="calibre11">"Fine work, Hen!" Kinnison strapped himself into his seat and donned
his headset. "General call! Attention! Battle stations! By stations,
report!"</p>
<p class="calibre11">"Station 1, tractor beams—hot!"</p>
<p class="calibre11">"Station 2, repellers—hot!"</p>
<p class="calibre11">"Station 3, projector 1—hot!"</p>
<p class="calibre11">Thus station after station of the warship of the void reported, until:
"Station 58, the Q-gun—hot!" Kinnison himself reported; then gave to
the pilot the words which throughout the space-ways of the galaxy had
come to mean complete readiness to face any emergency.</p>
<p class="calibre11">"Hot and tight, Hen—let's take 'em!"</p>
<p class="calibre11">The pilot shoved his blast lever, already almost at maximum, clear
out against its stop and hunched himself even more intently over his
instruments. As moved his pointers, so varied the direction of the
thrust that was driving the <i class="calibre12">Brittania</i> toward the enemy at the
unimaginable velocity of ninety parsecs an hour<a id="FNanchor_3_3"></a><span class="fnanchor" href="#/Text/Section0025#Footnote_3_3" title="Link target outside of Sample">[3]</span>—a velocity possible
only to inertialess matter being urged through an almost perfect vacuum
by a driving blast capable of lifting the stupendous normal tonnage of
the immense sky rover against a gravity ten times that of her native
Earth.</p>
<p class="calibre11">Unimaginable? Completely so—the ship of the Galactic Patrol was
hurling herself through space at a pace in comparison with which any
speed that the mind can grasp would be the merest crawl: a pace to make
light itself seem stationary.</p>
<p class="calibre11">Ordinary vision would have been useless, but the observers of that day
used no antiquated optical system. Their detector beams, converted into
light only at their plates, were heterodyned upon and were carried by
subethereal ultra-waves; vibrations residing far below the level of the
ether and thus possessing a velocity and a range infinitely greater
than those of any possible ether-borne wave.</p>
<p class="calibre11">Although stars moved across the visiplates in flaming, zigzag lines
of light, as pursued and pursuer passed system after solar system in
fantastic, light-years long hops, yet Henderson kept his cruiser upon
the pirate's tail and steadily cut down the distance between them. Soon
a tractor beam licked out from the patrol ship, touched the fleeing
marauder lightly, and the two space ships flashed toward each other.</p>
<p class="calibre11">Nor was the enemy unprepared for combat. One of the crack raiders of
Boskone, master pirate of the known universe, she had never before
found difficulty in conquering any vessel fleet enough to catch her.
Therefore, her commander made no attempt to cut the beam. Or rather,
since the two inertialess vessels flashed together to repeller-zone
contact in such a minute fraction of a second that any human action
within that time was impossible, it would be more correct to say that
the pirate captain changed his tactics instantly from those of flight
to those of combat.</p>
<p class="calibre11">He thrust out tractor beams of his own, and from the already white-hot
refractory throats of his projectors there raved out horribly potent
beams of annihilation, beams of dreadful power which tore madly at the
straining defensive screens of the patrol ship. Screens flared vividly,
radiating all the colors of the spectrum. Space itself seemed a rainbow
gone mad, for there were being exerted there forces of a magnitude to
stagger the imagination—forces to be yielded only by the atomic might
from which they sprang—forces whose neutralization set up visible
strains in the very fabric of the ether itself.</p>
<p class="calibre11">The young commander, seated at his conning plate, clenched his fists
and swore a startled, deep-space oath as his eyes swept over the
delicately accurate meters and gauges before him; for under the
frightful impact of that instantaneously launched attack his outer
screen was already down and his second was beginning to crack!</p>
<p class="calibre11">"We'll have to scrap the regulation battle plan!" he barked into his
microphone. "Open all motors to absolute top; cut all resistance out
of No. 3 Circuit. Dalhousie, cut all repellers, bring us right up to
their zone. All you beamers, concentrate on area K. <i class="calibre12">Break down those
screens!</i>" Kinnison was hunched rigidly over his panel; his voice came
grittily through locked teeth. "Cut <i class="calibre12">all</i> your resistors if you have
to, the motors and accumulators will hold long enough. There, that's
better. Our third is up again and theirs is going down. Come on, boys,
burn 'em down! Give 'em everything you can put through the bare bus
bars! No matter what it takes, get through to that wall shield, so that
I can use this Q-gun!"</p>
<p class="calibre11">Little by little, under the stupendous force of the <i class="calibre12">Brittania's</i>
attack the defenses of the enemy began to fail, and Kinnison's hands
flew over his controls. A port opened in the patrol ship's armored side
and an ugly snout protruded—the projector-ringed muzzle of a squat and
monstrous cannon. From its projector bands there leaped out, with the
velocity of light, a tube of quasi-solid force which was, in effect, a
continuation of the rifle's grim barrel; a tube which crashed through
the weakened third screen of the enemy with a space-racking shock and
struck savagely, with writhing, twisting thrusts, at the second.</p>
<p class="calibre11">Aided by the massed concentration of the <i class="calibre12">Brittania's</i> every battery
of short-range beams, it went through—and through the first. Now it
struck the very wall shield of the outlaw—that impregnable screen
which, designed to bear the brunt of any possible inert collision,
had never been pierced or ruptured by any material substance, however
applied.</p>
<p class="calibre11">To this inner defense the immaterial gun barrel clung. Simultaneously,
the tractor beams, hitherto exerting only a few dynes of force,
stiffened into unbreakable, inflexible rods of energy, binding the two
ships of space into one rigid system; each, relative to the other,
immovable.</p>
<p class="calibre11">Then Kinnison's flying finger tip touched a button and the Q-gun spoke.
From its sullen throat there erupted a huge torpedo. Slowly the giant
projectile crept along, watched in awe and amazement by the officers
of both vessels. For to those space-hardened veterans the velocity of
light was a veritable crawl; and here was a thing that would require
four or five whole seconds to cover a mere ten kilometers of distance!</p>
<div class="figcenter">
<img alt="" class="calibre13" id="id-5592705963062284481" src="Images/img0009.jpg"/>
<div class="pg_boilerplate">
<p class="calibre14"><i class="calibre12">Slowly the giant projectile crept along—watched in
awe and amazement by the officers of both vessels.</i></p>
</div>
</div>
<div class="figcenter">
<img alt="" class="calibre13" id="id-2748403887344739739" src="Images/img0010.jpg"/>
<div class="pg_boilerplate">
<p class="calibre14"><i class="calibre12">For to those space-hardened veterans the velocity of
light was a veritable crawl—and here was a thing that would require
four or five whole seconds.</i></p>
</div>
</div>
<p class="calibre11">But, although slow, this bomb <i class="calibre12">might</i> prove dangerous, therefore the
pirate commander threw his every resource into attempts to cut the tube
of force, to blast away from the tractor beams, to explode the sluggish
missile before it could reach his wall shields. In vain; for the
<i class="calibre12">Brittania's</i> every beam was set to protect the torpedo and the mighty
rods of energy without whose grip the inertialess mass of the enemy
vessel would offer no resistance whatever to the force of the proposed
explosion.</p>
<p class="calibre11">Slowly, <i class="calibre12">so</i> slowly, as the age-long seconds crawled into eternity,
there extended from enforcement vessel almost to pirate wall a
raging, white-hot-pillar—the gases of combustion of the propellent
heptadetonite—ahead of which there rushed the Q-gun's tremendous
shell with its horribly destructive freight. What would happen?
Could even the almost immeasurable force of that frightful charge of
duodecaplylatomate break down a wall shield designed to withstand the
cosmic assaults of meteoric missiles? And what would happen if that
wall screen held?</p>
<p class="calibre11">In spite of himself Kinnison's mind insisted upon painting the ghastly
picture: the awful explosion; the pirate's screen still intact; the
raving gases driven backward along the tube of force. The bare metal of
the Q-gun's breech, he knew, was not and could not be reënforced by the
infinitely stronger, although immaterial shields of pure energy which
protected the hull; and no conceivable substance, however resistant,
could impede, save momentarily, the unimaginable forces about to be
unleashed.</p>
<p class="calibre11">Nor would there be time to release the Q-tube after the explosion but
before the <i class="calibre12">Brittania's</i> own destruction; for if the enemy's shield
stayed up for even a fraction of a second, the unthinkable pressure
of the blast would propagate backward through the already densely
compressed gases in the tube, would sweep away as though it were
nothing the immensely thick metallic barrier of the gun breech, and
would wreak within the bowels of the patrol ship a destruction even
more complete than that intended for the foe.</p>
<p class="calibre11">Nor were his men in better case. Each knew that this was the climactic
instant of his whole existence; that life itself hung poised upon the
issue of the next split second. Hurry it up! Snap into it! Will that
crawling, creeping thing <i class="calibre12">never</i> strike? Some prayed briefly; some
swore bitterly; but prayers and curses were alike unconscious and had
precisely the same meaning—each man, white of face and grim of jaw,
clenched his hands and waited, tense and straining, for the impact.</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// Section0003.vue?505bae98